/**
 * Modal for printing a body or branch part: a few numbers in, a module
 * with bolt faces at the right places in the library.
 *
 * The presets follow what the robot_descriptions survey found about
 * trunks: limbs come in mirrored pairs (y -> -y), quadruped hips sit at
 * the four corners of a rectangle at the trunk's height, humanoid hips
 * hang below and shoulders sit above and beside the trunk.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ColorInput,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { apiClient, ApiClientError } from "../api/client";
import { BodyPreview } from "./BodyPreview";
import type { Dimension } from "./BodyPreview";
import type { PrintBodyPort, PrintBodySpec } from "../api/types";

type Preset = "legged" | "humanoid" | "branch" | "custom";
type Layout = "rows" | "radial";
type CoreShape = "box" | "hexagon" | "circle" | "hub";

/** The id the server will file the part under: ASCII letters, digits and underscores. */
export function moduleIdFor(name: string, fallback = "printed_body"): string {
  const id = name.replace(/[^A-Za-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
  return id || fallback;
}

/** What is wrong with a port list, or null when it can be printed. */
export function portProblem(ports: PrintBodyPort[]): string | null {
  if (ports.length === 0) return "A part needs at least one port.";
  const ids = ports.map((p, k) => moduleIdFor(p.name, `port${k + 1}`));
  const repeated = ids.filter((id, k) => ids.indexOf(id) !== k);
  if (repeated.length > 0) return `Port names must differ: ${[...new Set(repeated)].join(", ")} is used twice.`;
  return null;
}

interface PrintBodyModalProps {
  opened: boolean;
  onClose: () => void;
  onPrinted: (moduleId: string) => void;
  /** Editing a placed part: its spec fills the designer, and printing swaps the instance. */
  initial?: PrintBodySpec | null;
  replaceInstanceId?: string | null;
}

interface PresetNumbers {
  length: number;
  width: number;
  height: number;
  shoulderWidth: number;
  shoulderHeight: number;
  headHeight: number;
  branchCount: number;
  branchReach: number;
  legs: number;
}

const DEFAULTS: PresetNumbers = {
  length: 180,
  width: 60,
  height: 80,
  shoulderWidth: 100,
  shoulderHeight: 150,
  headHeight: 200,
  branchCount: 2,
  branchReach: 45,
  legs: 4,
};

function leggedPorts(n: PresetNumbers, layout: Layout): PrintBodyPort[] {
  // Every surveyed walker keeps its hips in mirrored pairs with the first
  // axis along the trunk (x), so a leg's face points outward.  Two rows
  // spread N/2 hips per side from +L to -L; radial puts N hips on a
  // circle of radius L, each facing out -- the shape of a hexapod or a
  // five-legged base.
  const legs = Math.max(2, Math.min(8, Math.round(n.legs)));
  const ports: PrintBodyPort[] = [];
  if (layout === "rows" && legs % 2 === 0) {
    const perSide = legs / 2;
    for (let k = 0; k < perSide; k += 1) {
      const x = perSide === 1 ? 0 : Math.round(n.length - (2 * n.length * k) / (perSide - 1));
      ports.push({ name: `leg${k + 1}_left`, xyz: [x, n.width, 0], rpy: [-90, 0, 0] });
      ports.push({ name: `leg${k + 1}_right`, xyz: [x, -n.width, 0], rpy: [90, 0, 0] });
    }
    return ports;
  }
  for (let k = 0; k < legs; k += 1) {
    // Start at the front (+x) and go round; mirror pairs fall out of the spacing.
    const angle = (360 / legs) * k;
    const rad = (angle * Math.PI) / 180;
    ports.push({
      name: `leg${k + 1}`,
      xyz: [Math.round(n.length * Math.cos(rad)), Math.round(n.length * Math.sin(rad)), 0],
      rpy: [0, 90, angle],
    });
  }
  return ports;
}

function humanoidPorts(n: PresetNumbers): PrintBodyPort[] {
  return [
    { name: "left_leg", xyz: [0, n.width, -n.height], rpy: [180, 0, 0] },
    { name: "right_leg", xyz: [0, -n.width, -n.height], rpy: [180, 0, 0] },
    { name: "left_arm", xyz: [0, n.shoulderWidth, n.shoulderHeight], rpy: [-90, 0, 0] },
    { name: "right_arm", xyz: [0, -n.shoulderWidth, n.shoulderHeight], rpy: [90, 0, 0] },
    { name: "head", xyz: [0, 0, n.headHeight], rpy: [0, 0, 0] },
  ];
}

function branchPorts(n: PresetNumbers): PrintBodyPort[] {
  // Outputs fanned around the input's axis, each facing outward.
  const count = Math.max(2, Math.min(6, Math.round(n.branchCount)));
  const ports: PrintBodyPort[] = [];
  for (let k = 0; k < count; k += 1) {
    const angle = (360 / count) * k;
    const rad = (angle * Math.PI) / 180;
    ports.push({
      name: `out${k + 1}`,
      xyz: [
        Math.round(n.branchReach * Math.cos(rad)),
        Math.round(n.branchReach * Math.sin(rad)),
        40,
      ],
      // Face outward: the port's z turned from +z toward the radial direction.
      rpy: [0, 90, angle],
    });
  }
  return ports;
}

export function PrintBodyModal({ opened, onClose, onPrinted, initial, replaceInstanceId }: PrintBodyModalProps) {
  const [preset, setPreset] = useState<Preset>("legged");
  const [layout, setLayout] = useState<Layout>("rows");
  const [coreShape, setCoreShape] = useState<CoreShape>("box");
  const [coreThickness, setCoreThickness] = useState(30);
  const [color, setColor] = useState("#c0c0c0");
  const [name, setName] = useState("my_body");
  const [numbers, setNumbers] = useState<PresetNumbers>(DEFAULTS);
  const [customPorts, setCustomPorts] = useState<PrintBodyPort[]>([
    { name: "port1", xyz: [0, 45, 40], rpy: [-90, 0, 0] },
    { name: "port2", xyz: [0, -45, 40], rpy: [90, 0, 0] },
  ]);
  const [customInput, setCustomInput] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printed, setPrinted] = useState<{ id: string; massG?: number; replaced?: boolean; swapped?: boolean; detail?: string } | null>(null);
  const small = useMediaQuery("(max-width: 600px)");

  const ports = useMemo<PrintBodyPort[]>(() => {
    switch (preset) {
      case "legged":
        return leggedPorts(numbers, layout);
      case "humanoid":
        return humanoidPorts(numbers);
      case "branch":
        return branchPorts(numbers);
      default:
        return customPorts;
    }
  }, [preset, layout, numbers, customPorts]);
  const isBranch = preset === "branch" || (preset === "custom" && customInput);
  // A placed part comes back into the designer as it was printed.
  useEffect(() => {
    if (!opened || !initial) return;
    setPreset("custom");
    setCustomPorts(initial.ports.map((p) => ({ ...p, xyz: [...p.xyz] as [number, number, number], rpy: [...p.rpy] as [number, number, number] })));
    setCustomInput(Boolean(initial.input));
    setName(initial.name);
    setCoreShape((initial.core_shape as CoreShape | undefined) ?? "box");
    if (initial.core_thickness) setCoreThickness(initial.core_thickness);
    if (initial.color) setColor(initial.color);
    setPrinted(null);
    setError(null);
  }, [opened, initial]);

  const savedAs = moduleIdFor(name);
  const problem = portProblem(ports);
  const dimensions = useMemo<Dimension[]>(() => {
    const n = numbers;
    const set = (key: keyof PresetNumbers) => (value: number) => setNumbers((cur) => ({ ...cur, [key]: value }));
    if (preset === "legged") {
      const radial = layout === "radial" || n.legs % 2 !== 0;
      if (radial) {
        return [{ key: "radius", label: "R", value: n.length, from: [0, 0, 0], to: [n.length, 0, 0], offset: [0, -60, 0], onChange: set("length") }];
      }
      return [
        { key: "length", label: "L", value: n.length, from: [0, n.width, 0], to: [n.length, n.width, 0], offset: [0, 70, 0], onChange: set("length") },
        { key: "width", label: "W", value: n.width, from: [n.length, 0, 0], to: [n.length, n.width, 0], offset: [70, 0, 0], onChange: set("width") },
      ];
    }
    if (preset === "humanoid") {
      return [
        { key: "hipW", label: "hip W", value: n.width, from: [0, 0, -n.height], to: [0, n.width, -n.height], offset: [0, 0, -50], onChange: set("width") },
        { key: "hipH", label: "hip drop", value: n.height, from: [0, n.width, 0], to: [0, n.width, -n.height], offset: [0, 70, 0], onChange: set("height") },
        { key: "shW", label: "shoulder W", value: n.shoulderWidth, from: [0, 0, n.shoulderHeight], to: [0, n.shoulderWidth, n.shoulderHeight], offset: [0, 0, 50], onChange: set("shoulderWidth") },
        { key: "shH", label: "shoulder H", value: n.shoulderHeight, from: [0, -n.shoulderWidth, 0], to: [0, -n.shoulderWidth, n.shoulderHeight], offset: [0, -70, 0], onChange: set("shoulderHeight") },
        { key: "headH", label: "head H", value: n.headHeight, from: [60, 0, 0], to: [60, 0, n.headHeight], offset: [60, 0, 0], onChange: set("headHeight") },
      ];
    }
    if (preset === "branch") {
      return [{ key: "reach", label: "reach", value: n.branchReach, from: [0, 0, 40], to: [n.branchReach, 0, 40], offset: [0, -50, 0], onChange: set("branchReach") }];
    }
    return [];
  }, [preset, layout, numbers]);

  const setNumber = (key: keyof PresetNumbers) => (value: string | number) => {
    const parsed = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isNaN(parsed)) {
      setNumbers((n) => ({ ...n, [key]: parsed }));
    }
  };

  const updateCustom = (index: number, field: "name" | "xyz" | "rpy", component: number, value: string | number) => {
    setCustomPorts((current) =>
      current.map((port, k) => {
        if (k !== index) return port;
        if (field === "name") return { ...port, name: String(value) };
        const parsed = typeof value === "number" ? value : parseFloat(value);
        if (Number.isNaN(parsed)) return port;
        const next = [...port[field]] as [number, number, number];
        next[component] = parsed;
        return { ...port, [field]: next };
      })
    );
  };

  const print = async () => {
    setBusy(true);
    setError(null);
    setPrinted(null);
    const spec: PrintBodySpec = {
      name,
      input: isBranch,
      ports,
      core_shape: coreShape,
      core_thickness: coreThickness,
      color,
      ...(replaceInstanceId ? { replace_instance_id: replaceInstanceId, session_id: window.sessionId } : {}),
    };
    try {
      const result = await apiClient.printBody(spec);
      if (result.success && result.module_id) {
        setPrinted({ id: result.module_id, massG: result.mass_g, replaced: result.replaced, swapped: result.replaced_instance, detail: result.detail });
        onPrinted(result.module_id);
      } else {
        setError(result.error ?? "Could not print the part");
      }
    } catch (e) {
      // The server says why in `detail`; the status alone helps nobody.
      setError(e instanceof ApiClientError && e.detail ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={replaceInstanceId ? "Edit the printed part" : "Print a body or branch"} size="lg" fullScreen={small}>
      <Stack gap="sm">
        <SegmentedControl
          value={preset}
          onChange={(value) => setPreset(value as Preset)}
          data={[
            { label: "Legged", value: "legged" },
            { label: "Humanoid", value: "humanoid" },
            { label: "Branch", value: "branch" },
            { label: "Custom", value: "custom" },
          ]}
        />
        <Group grow align="flex-end">
        <TextInput
          label="Part name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          description={
            savedAs === name ? undefined : `Saved as "${savedAs}" (letters, digits and underscores only)`
          }
        />
        <ColorInput
          label="Colour"
          value={color}
          onChange={setColor}
          format="hex"
          swatches={["#c0c0c0", "#4c4c4c", "#e8e4da", "#d15c6b", "#3f7ec2", "#3f9d6b", "#d9a13b"]}
        />
        </Group>

        {preset === "legged" && (
          <>
            <Group grow align="flex-end">
              <NumberInput label="Legs" value={numbers.legs} onChange={setNumber("legs")} min={2} max={8} step={1} />
              <SegmentedControl
                value={layout}
                onChange={(value) => setLayout(value as Layout)}
                data={[
                  { label: "Two rows", value: "rows", disabled: numbers.legs % 2 !== 0 },
                  { label: "Radial", value: "radial" },
                ]}
              />
            </Group>
            <Group grow>
              <NumberInput
                label={layout === "radial" || numbers.legs % 2 !== 0 ? "Radius (mm)" : "Half length L (mm)"}
                value={numbers.length}
                onChange={setNumber("length")}
                min={40}
              />
              {layout === "rows" && numbers.legs % 2 === 0 && (
                <NumberInput label="Half width W (mm)" value={numbers.width} onChange={setNumber("width")} min={30} />
              )}
            </Group>
          </>
        )}
        {preset === "humanoid" && (
          <>
            <Group grow>
              <NumberInput label="Hip half width (mm)" value={numbers.width} onChange={setNumber("width")} min={30} />
              <NumberInput label="Hip drop (mm)" value={numbers.height} onChange={setNumber("height")} min={0} />
            </Group>
            <Group grow>
              <NumberInput
                label="Shoulder half width (mm)"
                value={numbers.shoulderWidth}
                onChange={setNumber("shoulderWidth")}
                min={30}
              />
              <NumberInput
                label="Shoulder height (mm)"
                value={numbers.shoulderHeight}
                onChange={setNumber("shoulderHeight")}
                min={0}
              />
              <NumberInput label="Head height (mm)" value={numbers.headHeight} onChange={setNumber("headHeight")} min={0} />
            </Group>
          </>
        )}
        {preset === "branch" && (
          <Group grow>
            <NumberInput
              label="Outputs"
              value={numbers.branchCount}
              onChange={setNumber("branchCount")}
              min={2}
              max={6}
              step={1}
            />
            <NumberInput label="Reach (mm)" value={numbers.branchReach} onChange={setNumber("branchReach")} min={30} />
          </Group>
        )}
        {preset !== "custom" && (
          <Group>
            <Button
              size="xs"
              variant="light"
              onClick={() => {
                setCustomPorts(ports.map((port) => ({ ...port, xyz: [...port.xyz] as [number, number, number], rpy: [...port.rpy] as [number, number, number] })));
                setCustomInput(isBranch);
                setPreset("custom");
              }}
            >
              Edit these ports
            </Button>
            <Text size="xs" c="dimmed">
              Start from the preset, then move or turn any port.
            </Text>
          </Group>
        )}
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Core
          </Text>
          <SegmentedControl
            fullWidth
            value={coreShape}
            onChange={(value) => setCoreShape(value as CoreShape)}
            data={[
              { label: "Box", value: "box" },
              { label: "Hexagon plate", value: "hexagon" },
              { label: "Circle plate", value: "circle" },
              { label: "Hub + struts", value: "hub" },
            ]}
          />
          {coreShape !== "box" && (
            <NumberInput
              label="Core thickness (mm)"
              value={coreThickness}
              onChange={(v) => {
                const parsed = typeof v === "number" ? v : parseFloat(v);
                if (!Number.isNaN(parsed)) setCoreThickness(parsed);
              }}
              min={12}
            />
          )}
        </Stack>
        {preset === "custom" && (
          <>
            <Switch
              label="Branch (input face at the origin)"
              checked={customInput}
              onChange={(e) => setCustomInput(e.currentTarget.checked)}
            />
            <Group>
              <Button
                size="xs"
                variant="light"
                onClick={() =>
                  setCustomPorts((current) => [
                    ...current,
                    { name: `port${current.length + 1}`, xyz: [0, 0, 40], rpy: [0, 0, 0] },
                  ])
                }
              >
                Add port
              </Button>
              <Button
                size="xs"
                variant="light"
                disabled={customPorts.length <= 1}
                onClick={() => setCustomPorts((current) => current.slice(0, -1))}
              >
                Remove last
              </Button>
            </Group>
          </>
        )}

        <BodyPreview
          ports={ports}
          dimensions={dimensions}
          coreShape={coreShape}
          color={color}
          onPortChange={preset === "custom" ? (index, field, component, value) => updateCustom(index, field, component, value) : undefined}
        />
        <Box style={{ overflowX: "auto" }}>
        <Table fz="xs" withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>port</Table.Th>
              <Table.Th>x</Table.Th>
              <Table.Th>y</Table.Th>
              <Table.Th>z (mm)</Table.Th>
              <Table.Th>roll</Table.Th>
              <Table.Th>pitch</Table.Th>
              <Table.Th>yaw (deg)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ports.map((port, index) => (
              <Table.Tr key={index}>
                {preset === "custom" ? (
                  <>
                    <Table.Td>
                      <TextInput size="xs" value={port.name} onChange={(e) => updateCustom(index, "name", 0, e.currentTarget.value)} />
                    </Table.Td>
                    {[0, 1, 2].map((c) => (
                      <Table.Td key={`xyz${c}`}>
                        <NumberInput size="xs" value={port.xyz[c]} onChange={(v) => updateCustom(index, "xyz", c, v)} />
                      </Table.Td>
                    ))}
                    {[0, 1, 2].map((c) => (
                      <Table.Td key={`rpy${c}`}>
                        <NumberInput size="xs" value={port.rpy[c]} onChange={(v) => updateCustom(index, "rpy", c, v)} />
                      </Table.Td>
                    ))}
                  </>
                ) : (
                  <>
                    <Table.Td>{port.name}</Table.Td>
                    {port.xyz.map((v, c) => (
                      <Table.Td key={`xyz${c}`}>{v}</Table.Td>
                    ))}
                    {port.rpy.map((v, c) => (
                      <Table.Td key={`rpy${c}`}>{v}</Table.Td>
                    ))}
                  </>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Box>
        <Text size="xs" c="dimmed">
          Each port is a stock output face; a module's base bolts onto it, pointing out of the face. Roll, pitch and
          yaw turn the face about the part's x, y and z (degrees); x, y, z place it (mm).
          {isBranch ? " This part also has an input face at its origin." : " This part stands on its own."}
        </Text>
        {problem && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            {problem}
          </Alert>
        )}

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}
        {printed && (
          <Alert color="green" icon={<IconCheck size={16} />}>
            Printed {printed.id}
            {printed.massG !== undefined ? ` (${Math.round(printed.massG)} g of PLA)` : ""}; it is in the library now.
            {printed.replaced ? " It replaced the earlier part of that name." : ""}
            {printed.swapped
              ? " The placed part has been swapped for it; whatever was attached is still attached."
              : printed.detail
                ? ` ${printed.detail}`
                : " Close this, drag it into the scene, then turn on Connect (the plug button, bottom right) and click a port to attach a module there."}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
          <Button loading={busy} disabled={problem !== null} onClick={print}>
            Print
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
